/**
 * Aspect Admin — AI Jobs / generation queue.
 */

import { icon } from "./icons.js";
import { renderShell, pageHead, statusBadge, confidence, openDrawer, toast } from "./shell.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t } from "../i18n.js";
import { JOBS, QUEUE_HEALTH } from "../mock_data.js";

let state = { tab: "all" };

const TABS = [
  ["all", "All jobs"],
  ["running", "Running"],
  ["queued", "Queued"],
  ["done", "Completed"],
  ["failed", "Failed"],
];

function jobRow(j) {
  const progress =
    j.status === "running"
      ? `<div class="ap-progress ap-progress--live"><div class="ap-meter__track"><div class="ap-meter__fill" style="width:${j.progress}%"></div></div><span class="ap-progress__pct">${j.progress}%</span></div>`
      : j.status === "queued"
        ? `<span class="ap-note">${t(j.eta)}</span>`
        : j.status === "failed"
          ? `<span class="ap-badge ap-badge--err">${icon("alert", 11)} ${t("Error")}</span>`
          : `<span class="ap-note">${t("Done")}</span>`;
  return `
    <tr data-row data-job="${j.id}">
      <td>
        <div class="ap-entity">
          <div class="ap-avatar">${icon(j.type.includes("analysis") ? "eye" : j.type.includes("export") ? "download" : "wand", 15)}</div>
          <div>
            <span class="ap-cell-main">${escapeHtml(t(j.type))}</span>
            <span class="ap-cell-sub">${escapeHtml(j.space)} · ${escapeHtml(j.client)}</span>
          </div>
        </div>
      </td>
      <td>${statusBadge(j.status)}</td>
      <td>${progress}</td>
      <td><span class="ap-codechip" style="padding:0.18rem 0.5rem;">${j.model}</span></td>
      <td>${confidence(j.conf)}</td>
      <td>${t(j.started)}</td>
      <td class="ap-cell-actions">
        ${j.status === "failed" ? `<button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-act="retry">${icon("refresh", 13)} ${t("Retry")}</button>` : ""}
        <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm" data-act="trace">${t("Trace")} ${icon("chevronRight", 12)}</button>
      </td>
    </tr>
  `;
}

function traceDrawer(root, j) {
  const steps =
    j.status === "failed"
      ? [
          [t("Input received"), t("Source image 640×420 · 82 KB"), "ok"],
          [t("Preprocessing"), t("Upscaled 2× · denoised · contrast normalized"), "ok"],
          [t("Wall detection"), t("Detected 14 segments · 3 ambiguous junctions"), "warn"],
          [t("Confidence gate"), t("Score 0.41 — below 0.50 threshold. Job halted before scene build."), "err"],
        ]
      : [
          [t("Input received"), t("Space \"{s}\" · spec v3", { s: j.space }), "ok"],
          [t("Scene graph built"), t("9 rooms · 42 anchor points resolved"), "ok"],
          [t("Model inference"), t("{m} · 24 placements proposed", { m: j.model }), "ok"],
          [t("Validation"), t("Collision check passed · confidence {c}", { c: j.conf ?? "—" }), "ok"],
        ];
  openDrawer(root, {
    title: t("Job {id}", { id: j.id }),
    subtitle: `${t(j.type)} · ${j.space}`,
    bodyHtml: `
      <div class="ap-drawer__section">
        <h4>${t("Summary")}</h4>
        <dl class="ap-def">
          <dt>${t("Status")}</dt><dd>${statusBadge(j.status)}</dd>
          <dt>${t("Model")}</dt><dd><span class="ap-codechip" style="padding:0.18rem 0.5rem;">${j.model}</span></dd>
          <dt>${t("Client")}</dt><dd>${escapeHtml(j.client)}</dd>
          <dt>${t("Confidence")}</dt><dd>${confidence(j.conf)}</dd>
          <dt>${t("Started")}</dt><dd>${t(j.started)}</dd>
        </dl>
      </div>
      ${j.error ? `<div class="ap-insight" style="border-color:rgba(214,69,65,0.3);background:radial-gradient(ellipse 90% 90% at 0% 0%, rgba(214,69,65,0.07) 0%, transparent 55%), rgba(255,255,255,0.72);">
        <div class="ap-insight__head">${icon("alert", 15)}<span class="ap-insight__tag" style="color:var(--ap-err);">${t("Failure reason")}</span></div>
        <p class="ap-insight__body">${escapeHtml(t(j.error))}</p>
        <div class="ap-insight__foot"><span></span><button type="button" class="ap-btn ap-btn--soft ap-btn--sm" data-drawer-retry>${icon("refresh", 13)} ${t("Retry with enhanced input")}</button></div>
      </div>` : ""}
      <div class="ap-drawer__section">
        <h4>${t("Execution trace")}</h4>
        <div class="ap-timeline">
          ${steps
            .map(
              ([tt, d, k]) => `
            <div class="ap-timeline__item ${k === "ok" ? "ap-timeline__item--mute" : ""}">
              <strong>${tt}</strong>${k === "err" ? ` <span class="ap-badge ap-badge--err" style="margin-left:0.3rem;">${t("Halted")}</span>` : k === "warn" ? ` <span class="ap-badge ap-badge--warn" style="margin-left:0.3rem;">${t("Review")}</span>` : ""}
              <br />${d}
              <span class="ap-timeline__time">—</span>
            </div>`
            )
            .join("")}
        </div>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Explainability")}</h4>
        <p class="ap-note" style="margin:0;">${t("Every placement decision is logged with its input features and alternatives considered. Full traces are retained for 30 days on the Scale plan.")}</p>
      </div>
    `,
    footHtml: `<button type="button" class="ap-btn" data-overlay-close-2>${t("Close")}</button>
      <button type="button" class="ap-btn ap-btn--primary" data-drawer-open-space>${icon("cube", 15)} ${t("Open space")}</button>`,
  });
  const slot = root.querySelector("#ap-overlay-slot");
  slot?.querySelector("[data-drawer-retry]")?.addEventListener("click", () => toast(root, t("Job {id} requeued with 2× upscaling", { id: j.id }), "ok"));
  slot?.querySelector("[data-overlay-close-2]")?.addEventListener("click", () => (slot.innerHTML = ""));
  slot?.querySelector("[data-drawer-open-space]")?.addEventListener("click", () => navigate("/spaces"));
}

export function renderJobs(root) {
  const session = getSession();
  if (!session) { navigate("/login", true); return; }

  const jobs = state.tab === "all" ? JOBS : JOBS.filter((j) => j.status === state.tab);

  const health = [
    [t("Queue depth"), t("{n} jobs", { n: QUEUE_HEALTH.depth }), "layers"],
    [t("Workers"), QUEUE_HEALTH.workers, "cpu"],
    [t("p95 latency"), QUEUE_HEALTH.p95, "bolt"],
    [t("Success rate (7d)"), `${QUEUE_HEALTH.successRate}%`, "check"],
  ];

  const content = `
    ${pageHead(t("AI Jobs"), t("Generation queue, processing status, and execution traces."), `
      <button type="button" class="ap-btn" id="jb-pause">${icon("pause", 15)} ${t("Pause queue")}</button>
      <button type="button" class="ap-btn ap-btn--primary" id="jb-new">${icon("wand", 15)} ${t("New generation")}</button>
    `)}

    <div class="ap-grid ap-grid--kpi" style="margin-bottom:1.1rem;">
      ${health
        .map(
          ([label, value, ic]) => `
        <div class="ap-panel ap-kpi">
          <div class="ap-kpi__top">
            <span class="ap-kpi__label">${label}</span>
            <span class="ap-kpi__icon">${icon(ic, 16)}</span>
          </div>
          <div class="ap-kpi__value" style="font-size:1.5rem;">${value}</div>
        </div>`
        )
        .join("")}
    </div>

    <div class="ap-panel">
      <div class="ap-panel__head">
        <div class="ap-seg">
          ${TABS.map(([id, label]) => `<button type="button" class="ap-seg__btn ${state.tab === id ? "ap-seg__btn--on" : ""}" data-tab="${id}">${t(label)}</button>`).join("")}
        </div>
        <span class="ap-badge ap-badge--cyan"><span class="ap-dot ap-dot--pulse"></span>${t("Live · auto-refreshing")}</span>
      </div>
      <div class="ap-tablewrap ap-scroll">
        <table class="ap-table">
          <thead><tr><th>${t("Job")}</th><th>${t("Status")}</th><th>${t("Progress")}</th><th>${t("Model")}</th><th>${t("Confidence")}</th><th>${t("Started")}</th><th></th></tr></thead>
          <tbody>${jobs.map(jobRow).join("")}</tbody>
        </table>
      </div>
      <div class="ap-panel__foot">
        <span>${t("{n} jobs shown", { n: jobs.length })}</span>
        <span>${t("Traces retained 30 days")}</span>
      </div>
    </div>
  `;

  const el = renderShell(root, "/jobs", content);
  if (!el) return;

  el.querySelectorAll("[data-tab]").forEach((tab) =>
    tab.addEventListener("click", () => {
      state = { tab: tab.dataset.tab };
      renderJobs(root);
    })
  );

  el.querySelector("#jb-pause")?.addEventListener("click", () => toast(root, t("Queue paused — running jobs will finish"), "info"));
  el.querySelector("#jb-new")?.addEventListener("click", () => toast(root, t("Pick a space to run a generation on"), "info"));

  el.querySelector("tbody")?.addEventListener("click", (e) => {
    const row = e.target.closest("[data-job]");
    if (!row) return;
    const job = JOBS.find((j) => j.id === row.dataset.job);
    if (!job) return;
    const act = e.target.closest("[data-act]")?.dataset?.act;
    if (act === "retry") {
      toast(root, t("Job {id} requeued", { id: job.id }), "ok");
      return;
    }
    traceDrawer(root, job);
  });
}
